// =============================================================================
// Component: ReportHistoryDrawer
// Shows version history of generated reports for a submission
// =============================================================================

import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  History,
  Download,
  Loader2,
  AlertTriangle,
  Clock,
  FileText,
  HardDrive,
  Timer,
} from "lucide-react";
import { useReportQueue, type GeneratedReport } from "@/hooks/useReportQueue";
import { formatDistanceToNow, format } from "date-fns";
import { es } from "date-fns/locale";

interface ReportHistoryDrawerProps {
  submissionId: number;
  templateId: number;
  submissionName?: string;
  trigger?: React.ReactNode;
}

export function ReportHistoryDrawer({
  submissionId,
  templateId,
  submissionName,
  trigger,
}: ReportHistoryDrawerProps) {
  const [open, setOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { useReportsBySubmission, downloadReport } = useReportQueue();
  const { data: allReports, isLoading } = useReportsBySubmission(submissionId);

  // Filter reports for this template and sort by version desc
  const reports = (allReports || [])
    .filter((r) => r.template_id === templateId)
    .sort((a, b) => b.version - a.version);

  const handleDownload = async (report: GeneratedReport) => {
    setDownloadingId(report.id);
    try {
      const fileName = submissionName
        ? `reporte_${submissionName.replace(/[^a-zA-Z0-9-_]/g, "_")}_v${report.version}.pptx`
        : `reporte_${submissionId}_v${report.version}.pptx`;
      await downloadReport(report.id, fileName);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatGenerationTime = (ms: number | null): string => {
    if (!ms) return "—";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm">
            <History className="h-4 w-4 mr-2" />
            Historial
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[450px] sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Historial de Reportes
          </SheetTitle>
          <SheetDescription>
            {submissionName
              ? `Versiones generadas para ${submissionName}`
              : `Versiones del reporte #${submissionId}`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                No hay reportes generados
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-200px)]">
              <div className="space-y-3 pr-4">
                {reports.map((report, index) => (
                  <div key={report.id}>
                    <div
                      className={`
                        rounded-lg border p-4 transition-colors
                        ${report.is_outdated ? "border-yellow-500/50 bg-yellow-500/5" : ""}
                        ${index === 0 ? "border-primary/50 bg-primary/5" : ""}
                      `}
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant={index === 0 ? "default" : "secondary"}>
                            v{report.version}
                          </Badge>
                          {index === 0 && (
                            <Badge variant="outline" className="text-xs">
                              Última
                            </Badge>
                          )}
                          {report.is_outdated && (
                            <Badge variant="outline" className="text-yellow-600 border-yellow-500/50">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Desactualizado
                            </Badge>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownload(report)}
                          disabled={downloadingId === report.id}
                        >
                          {downloadingId === report.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      </div>

                      {/* Metadata */}
                      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          <span title={format(new Date(report.created_at), "PPpp", { locale: es })}>
                            {formatDistanceToNow(new Date(report.created_at), {
                              addSuffix: true,
                              locale: es,
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <HardDrive className="h-3.5 w-3.5" />
                          <span>{formatFileSize(report.file_size_bytes)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 col-span-2">
                          <Timer className="h-3.5 w-3.5" />
                          <span>
                            Generado en {formatGenerationTime(report.generation_time_ms)}
                          </span>
                        </div>
                      </div>

                      {/* File name */}
                      <div className="mt-2 text-xs text-muted-foreground truncate">
                        {report.file_name}
                      </div>
                    </div>
                    {index < reports.length - 1 && (
                      <Separator className="my-3" />
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Summary footer */}
        {reports.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-background">
            <p className="text-xs text-muted-foreground text-center">
              {reports.length} versión{reports.length !== 1 ? "es" : ""} generada
              {reports.length !== 1 ? "s" : ""}
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
